// src/screens/HomeScreen.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  IonContent, IonPage
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { 
  Bell, Shield, Heart, TrendingUp, Sparkles, ChevronRight,
  Wallet, Landmark, Plane, Umbrella, Building2, Scale, BookOpen,
  FileText, Leaf, Activity, Loader2, LogIn, ArrowUpRight,
  GraduationCap, BarChart3, PiggyBank, Target,
  Crown, Mail, Phone, MapPin, Calendar, Star, CheckCircle2
} from 'lucide-react';
import { capacitorDbService } from '../services/capacitorDb.service';
import { useAuthStore } from '../store/authStore';
import { useWealthData, triggerWealthSync } from '../hooks/useWealthData';
import { wealthSync } from '../services/wealthSync.service';

interface PillarItem {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color: string;
  bgColor: string;
  route: string;
  status?: string;
  statusColor?: string;
  progress?: number;
}

interface QuickAction {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color: string;
  route: string;
  badge?: string;
  badgeColor?: string;
}

interface AtAGlanceItem {
  label: string;
  value: string;
  subValue: string;
  status: 'good' | 'medium' | 'needs-attention' | 'on-track' | 'below-target';
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

interface RecommendedAction {
  id: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  subtitle: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
  route: string;
}

// ============================================================
// COLOR PALETTE - New Design System
// ============================================================
const COLORS = {
  deepGreen: '#0B3E2E',
  green: '#1B5A40',
  lightGreen: '#22C55E',
  gold: '#D4AF37',
  cream: '#F5F5DC',
  creamLight: '#FAF8F0',
  white: '#FFFFFF',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray800: '#1F2937',
  red: '#EF4444',
  redLight: '#FEF2F2',
  amber: '#F59E0B',
  amberLight: '#FFFBEB',
  blue: '#3B82F6',
  blueLight: '#EFF6FF',
  purple: '#8B5CF6',
  purpleLight: '#F3F0FF',
  orange: '#F97316',
  orangeLight: '#FFF7ED',
  teal: '#14B8A6',
  tealLight: '#F0FDFA',
  indigo: '#6366F1',
  indigoLight: '#EEF2FF',
};

// ============================================================
// SAFE CALCULATIONS
// ============================================================
const toNumber = (val: any): number => {
  if (val === null || val === undefined || val === '') return 0;
  const num = Number(val);
  return isNaN(num) ? 0 : num;
};

const calculateRetirementProjection = (plan: any) => {
  if (!plan) return null;
  const currentAge = toNumber(plan.currentAge);
  const retirementAge = toNumber(plan.retirementAge);
  const currentSavings = toNumber(plan.currentSavings);
  const monthlyContribution = toNumber(plan.monthlyContribution);
  const monthlyExpenses = toNumber(plan.monthlyExpenses || plan.expectedMonthlyExpenses);
  const lifeExpectancy = toNumber(plan.lifeExpectancy || 80);
  const expectedReturnRate = toNumber(plan.expectedReturnRate || plan.returnRate || 6);
  const inflationRate = toNumber(plan.inflationRate || 3.5);

  const yearsToRetirement = retirementAge - currentAge;
  const yearsInRetirement = lifeExpectancy - retirementAge;

  if (yearsToRetirement <= 0 || yearsInRetirement <= 0) {
    return { progressPercent: 0, monthlyIncome: 0, onTrack: false };
  }

  const futureSavings = currentSavings * Math.pow(1 + expectedReturnRate / 100, yearsToRetirement);
  const monthlyRate = expectedReturnRate / 100 / 12;
  const months = yearsToRetirement * 12;
  const futureContributions = monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate);
  const totalProjected = futureSavings + futureContributions;
  const inflatedMonthlyExpenses = monthlyExpenses * Math.pow(1 + inflationRate / 100, yearsToRetirement);
  const totalNeeded = inflatedMonthlyExpenses * 12 * yearsInRetirement;
  const progressPercent = totalNeeded > 0 ? Math.round((totalProjected / totalNeeded) * 100) : 0;
  const monthlyIncome = yearsInRetirement > 0 ? Math.round(totalProjected / (yearsInRetirement * 12)) : 0;

  return { 
    progressPercent: Math.min(100, progressPercent), 
    monthlyIncome, 
    onTrack: totalProjected >= totalNeeded 
  };
};

const calculateTakafulScore = (plan: any) => {
  if (!plan) return null;
  const existingCoverage = toNumber(plan.existingCoverage);
  const recommendedCoverage = toNumber(plan.recommendedCoverage);
  const coverageRatio = existingCoverage / (recommendedCoverage || 1);
  const progressPercent = Math.min(100, Math.round(coverageRatio * 100));
  return { progressPercent, gap: Math.max(0, recommendedCoverage - existingCoverage) };
};

const calculateOverallHealth = (retirement: any, takaful: any, assets: any, liabilities: any) => {
  let score = 0;
  let count = 0;
  if (retirement?.progressPercent !== undefined) { score += retirement.progressPercent; count++; }
  if (takaful?.progressPercent !== undefined) { score += takaful.progressPercent; count++; }
  if (assets?.totalValue > 0) { score += Math.min(100, 50 + (assets.totalValue / 100000)); count++; }
  if (liabilities?.totalRemaining > 0) { score += Math.max(0, 100 - (liabilities.totalRemaining / 50000)); count++; }
  return count > 0 ? Math.round(score / count) : 50;
};

// ============================================================
// PLATFORM & SAFE-AREA UTILITIES (iOS/Android)
// ============================================================
const getPlatform = (): 'ios' | 'android' | 'web' => {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'web';
};

const useSafeArea = () => {
  const [insets, setInsets] = React.useState({ top: 0, bottom: 0 });

  React.useEffect(() => {
    const platform = getPlatform();
    const style = getComputedStyle(document.documentElement);

    const readInsets = () => {
      const rawTop = style.getPropertyValue('--sat').trim() || style.getPropertyValue('env(safe-area-inset-top)').trim();
      const rawBottom = style.getPropertyValue('--sab').trim() || style.getPropertyValue('env(safe-area-inset-bottom)').trim();
      const top = parseInt(rawTop) || (platform === 'ios' ? 44 : platform === 'android' ? 24 : 0);
      const bottom = parseInt(rawBottom) || (platform === 'ios' ? 34 : platform === 'android' ? 16 : 0);
      setInsets({ top, bottom });
    };

    // Inject CSS custom properties for safe-area
    const meta = document.createElement('style');
    meta.id = 'nurwallet-safe-area';
    meta.textContent = `
      :root {
        --sat: env(safe-area-inset-top, 0px);
        --sab: env(safe-area-inset-bottom, 0px);
        --sal: env(safe-area-inset-left, 0px);
        --sar: env(safe-area-inset-right, 0px);
      }
      /* Responsive grid helpers */
      .nw-grid-5 {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 10px;
      }
      .nw-grid-2 {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
      }
      @media (max-width: 374px) {
        .nw-grid-5 { grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .nw-grid-2 { grid-template-columns: 1fr; gap: 10px; }
      }
      @media (min-width: 768px) {
        .nw-grid-5 { grid-template-columns: repeat(5, 1fr); }
        .nw-grid-2 { grid-template-columns: repeat(2, 1fr); }
        .nw-max-w { max-width: 640px; margin-left: auto; margin-right: auto; }
      }
      @media (min-width: 1024px) {
        .nw-max-w { max-width: 800px; }
      }
      /* Reduced motion */
      @media (prefers-reduced-motion: reduce) {
        * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
      }
    `;
    if (!document.getElementById('nurwallet-safe-area')) {
      document.head.appendChild(meta);
    }

    readInsets();
    window.addEventListener('resize', readInsets);
    return () => window.removeEventListener('resize', readInsets);
  }, []);

  return insets;
};

// ============================================================
// SVG CIRCULAR PROGRESS COMPONENT
// ============================================================
const CircularProgress: React.FC<{ percentage: number; size?: number; strokeWidth?: number; color?: string }> = React.memo(({ 
  percentage, size = 120, strokeWidth = 8, color = COLORS.gold 
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <defs>
        <linearGradient id={`progressGradient-${percentage}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={COLORS.gold} />
          <stop offset="100%" stopColor={COLORS.lightGreen} />
        </linearGradient>
      </defs>
      <circle 
        cx={size / 2} 
        cy={size / 2} 
        r={radius} 
        stroke="rgba(255,255,255,0.15)" 
        strokeWidth={strokeWidth} 
        fill="none" 
      />
      <circle 
        cx={size / 2} 
        cy={size / 2} 
        r={radius} 
        stroke={`url(#progressGradient-${percentage})`} 
        strokeWidth={strokeWidth} 
        fill="none" 
        strokeDasharray={circumference} 
        strokeDashoffset={offset} 
        strokeLinecap="round" 
        style={{ transition: 'stroke-dashoffset 1s ease-out' }} 
      />
    </svg>
  );
});

// ============================================================
// UI COMPONENTS - NEW DESIGN
// ============================================================

const Header = React.memo(({ userName, isPremium, onProfile, onNotifications }: { 
  userName: string; isPremium: boolean; onProfile: () => void; onNotifications: () => void 
}) => {
  const { top: safeTop } = useSafeArea();
  return (
  <div style={{ padding: `max(${safeTop}px, 16px) 20px 12px` }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div 
          onClick={onProfile}
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${COLORS.deepGreen} 0%, ${COLORS.green} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.white,
            fontWeight: 700,
            fontSize: '16px',
            cursor: 'pointer',
            boxShadow: `0 4px 12px rgba(11, 62, 46, 0.3)`,
            border: `2px solid ${COLORS.gold}`
          }}
        >
          {userName.charAt(0).toUpperCase()}
        </div>
        <div>
          <p style={{ fontSize: '12px', color: COLORS.gray500, margin: '0 0 2px' }}>Assalamualaikum,</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 700, fontSize: '18px', color: COLORS.gray800, fontFamily: 'Georgia, serif' }}>
              {userName}
            </span>
            {isPremium && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 600,
                color: COLORS.deepGreen,
                background: `linear-gradient(135deg, ${COLORS.gold}20, ${COLORS.gold}40)`,
                border: `1px solid ${COLORS.gold}`
              }}>
                <Crown size={12} color={COLORS.gold} />
                Premium
              </span>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={onNotifications}
        style={{
          position: 'relative',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: COLORS.white,
          border: `1px solid ${COLORS.gray200}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        <Bell size={20} color={COLORS.gray600} />
        <span style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: COLORS.red,
          border: `2px solid ${COLORS.white}`
        }} />
      </button>
    </div>
    <p style={{ 
      fontSize: '13px', 
      color: COLORS.gray500, 
      margin: '8px 0 0',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      May Allah bless your wealth and protect your family.
      <Heart size={14} color={COLORS.lightGreen} fill={COLORS.lightGreen} />
    </p>
  </div>
  );
});

const Skeleton = ({ width, height, borderRadius = '6px', style }: { 
  width: string; height: string; borderRadius?: string; style?: React.CSSProperties 
}) => (
  <div style={{
    width,
    height,
    borderRadius,
    background: '#E5E7EB',
    animation: 'nw-pulse 1.6s ease-in-out infinite',
    ...style
  }} />
);

const FinancialHealthCard = React.memo(({ score, percentile, isLoading }: { score: number; percentile: number; isLoading: boolean }) => {
  const history = useHistory();
  const getScoreLabel = (s: number) => {
    if (s >= 80) return 'Excellent';
    if (s >= 60) return 'Good Progress';
    if (s >= 40) return 'Needs Attention';
    return 'Critical';
  };
  const getScoreColor = (s: number) => {
    if (s >= 80) return COLORS.lightGreen;
    if (s >= 60) return COLORS.gold;
    if (s >= 40) return COLORS.amber;
    return COLORS.red;
  };

  const handleViewReport = useCallback(() => {
    history.push('/dashboard');
  }, [history]);

  return (
    <div style={{ padding: '0 20px', marginBottom: '20px' }}>
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.deepGreen} 0%, ${COLORS.green} 100%)`,
        borderRadius: '24px',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `0 8px 32px rgba(11, 62, 46, 0.25)`
      }}>
        {/* Decorative circles */}
        <div style={{
          position: 'absolute',
          top: '-40px',
          right: '-40px',
          width: '160px',
          height: '160px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-20px',
          left: '-20px',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
          zIndex: 0
        }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'nowrap', minWidth: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'uppercase',
                letterSpacing: '1.5px'
              }}>
                Financial Health Score
              </span>
              <Activity size={14} color="rgba(255,255,255,0.5)" />
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '12px' }}>
              {isLoading ? (
                <Skeleton width="80px" height="48px" borderRadius="8px" style={{ background: 'rgba(255,255,255,0.2)' }} />
              ) : (
                <>
                  <span style={{ fontSize: 'clamp(36px, 10vw, 48px)', fontWeight: 800, color: COLORS.white, lineHeight: 1 }}>
                    {score}
                  </span>
                  <span style={{ fontSize: 'clamp(16px, 5vw, 20px)', color: 'rgba(255,255,255,0.5)', fontWeight: 400 }}>/100</span>
                </>
              )}
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '20px',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              marginBottom: '12px'
            }}>
              {isLoading ? (
                <Skeleton width="100px" height="20px" borderRadius="10px" style={{ background: 'rgba(255,255,255,0.2)' }} />
              ) : (
                <>
                  <ArrowUpRight size={14} color={COLORS.lightGreen} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: COLORS.white }}>
                    {getScoreLabel(score)}
                  </span>
                </>
              )}
            </div>

            {isLoading ? (
              <Skeleton width="70%" height="16px" borderRadius="8px" style={{ background: 'rgba(255,255,255,0.2)' }} />
            ) : (
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                You're ahead of {percentile}% of users in your age group.
              </p>
            )}
          </div>

          <div style={{ position: 'relative', marginLeft: '16px' }}>
            <CircularProgress percentage={score} size={100} strokeWidth={7} />
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <Shield size={28} color={COLORS.white} />
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handleViewReport}
          style={{
            width: '100%',
            marginTop: '20px',
            padding: '14px',
            borderRadius: '16px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: COLORS.white,
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            cursor: 'pointer',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.2s'
          }}
        >
          View Full Report
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
});

const AIInsightCard = React.memo(({ title, message, cta, route }: { 
  title: string; message: string; cta: string; route: string 
}) => {
  const history = useHistory();
  const handleClick = useCallback(() => {
    history.push(route);
  }, [history, route]);

  return (
    <div style={{ padding: '0 20px', marginBottom: '20px' }}>
      <div style={{
        background: COLORS.white,
        borderRadius: '24px',
        padding: '20px',
        border: `1px solid ${COLORS.gray200}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${COLORS.green}15, ${COLORS.lightGreen}15)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Sparkles size={24} color={COLORS.green} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <span style={{
                fontSize: '11px',
                fontWeight: 700,
                color: COLORS.green,
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                AI Insight
              </span>
              <Sparkles size={12} color={COLORS.gold} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: COLORS.gray800, margin: '0 0 6px' }}>
              {title}
            </h3>
            <p style={{ fontSize: '13px', color: COLORS.gray500, lineHeight: 1.5, margin: '0 0 12px' }}>
              {message}
            </p>
            <button
              onClick={handleClick}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                borderRadius: '14px',
                background: `linear-gradient(135deg, ${COLORS.deepGreen} 0%, ${COLORS.green} 100%)`,
                color: COLORS.white,
                fontSize: '13px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                boxShadow: `0 4px 16px rgba(11, 62, 46, 0.25)`
              }}
            >
              {cta}
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const AtAGlance = React.memo(({ items, isLoading }: { items: AtAGlanceItem[]; isLoading: boolean }) => {
  const history = useHistory();
  const handleCustomize = useCallback(() => {
    history.push('/dashboard');
  }, [history]);

  return (
    <div style={{ padding: '0 20px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: COLORS.gray800, margin: 0 }}>
          At a Glance
        </h3>
        <button
          onClick={handleCustomize}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
            fontWeight: 600,
            color: COLORS.green,
            cursor: 'pointer'
          }}
        >
          Customize
          <Sparkles size={14} color={COLORS.gold} />
        </button>
      </div>
      <div className="nw-grid-2">
        {(isLoading ? Array(4).fill(null) : items).map((item: any, index: number) => (
          <div key={index} style={{
            background: COLORS.white,
            borderRadius: '20px',
            padding: '16px',
            border: `1px solid ${COLORS.gray100}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: item?.iconBg || 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '10px'
            }}>
              {isLoading ? (
                <Skeleton width="20px" height="20px" borderRadius="6px" />
              ) : (
                React.cloneElement(item.icon as React.ReactElement, { size: 20, color: item.iconColor })
              )}
            </div>
            <p style={{ fontSize: '12px', color: COLORS.gray500, margin: '0 0 4px' }}>
              {isLoading ? <Skeleton width="70%" height="14px" borderRadius="4px" /> : item.label}
            </p>
            <p style={{ fontSize: '16px', fontWeight: 700, color: COLORS.gray800, margin: '0 0 4px' }}>
              {isLoading ? <Skeleton width="60%" height="18px" borderRadius="4px" /> : item.value}
            </p>
            <p style={{ 
              fontSize: '11px', 
              fontWeight: 600,
              color: item?.status === 'good' || item?.status === 'on-track' ? COLORS.green : 
                     item?.status === 'medium' ? COLORS.amber : COLORS.red,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {isLoading ? (
                <Skeleton width="50%" height="12px" borderRadius="4px" />
              ) : (
                <>
                  {item.status === 'good' || item.status === 'on-track' ? <ArrowUpRight size={12} /> :
                   item.status === 'medium' ? <TrendingUp size={12} /> : <ChevronRight size={12} />}
                  {item.subValue}
                </>
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
});

const FinancialPillars = React.memo(({ pillars }: { pillars: PillarItem[] }) => {
  const history = useHistory();

  const handleViewAll = useCallback(() => {
    history.push('/assessment');
  }, [history]);

  return (
    <div style={{ padding: '0 20px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: COLORS.gray800, margin: 0 }}>
          Your Financial Pillars
        </h3>
        <button
          onClick={handleViewAll}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
            fontWeight: 600,
            color: COLORS.green,
            cursor: 'pointer'
          }}
        >
          View All
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="nw-grid-5">
        {pillars.map((pillar) => (
          <button
            key={pillar.id}
            onClick={() => history.push(pillar.route)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 8px',
              borderRadius: '16px',
              background: COLORS.white,
              border: `1px solid ${COLORS.gray100}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '14px',
              background: pillar.bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {React.cloneElement(pillar.icon as React.ReactElement, { size: 20, color: pillar.color })}
              {pillar.status && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: pillar.statusColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 700,
                  color: COLORS.white,
                  border: `2px solid ${COLORS.white}`
                }}>
                  {pillar.status}
                </span>
              )}
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '11px', fontWeight: 600, color: COLORS.gray800, margin: '0 0 2px' }}>
                {pillar.title}
              </p>
              <p style={{ fontSize: '10px', color: COLORS.gray500, margin: 0 }}>
                {pillar.subtitle}
              </p>
            </div>
            {pillar.progress !== undefined && (
              <div style={{ width: '100%', marginTop: '4px' }}>
                <div style={{
                  width: '100%',
                  height: '3px',
                  borderRadius: '2px',
                  background: COLORS.gray100,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    borderRadius: '2px',
                    width: `${pillar.progress}%`,
                    background: pillar.color,
                    transition: 'width 0.5s ease-out'
                  }} />
                </div>
                <p style={{ fontSize: '10px', color: pillar.color, fontWeight: 600, margin: '2px 0 0', textAlign: 'center' }}>
                  {pillar.progress}%
                </p>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
});

const RecommendedActions = React.memo(({ actions }: { actions: RecommendedAction[] }) => {
  const history = useHistory();

  return (
    <div style={{ padding: '0 20px', marginBottom: '20px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 700, color: COLORS.gray800, margin: '0 0 12px' }}>
        Recommended Actions
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {actions.map((action) => (
          <button
            key={action.id}
            onClick={() => history.push(action.route)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '16px',
              borderRadius: '20px',
              background: COLORS.white,
              border: `1px solid ${COLORS.gray100}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'left',
              width: '100%'
            }}
          >
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '14px',
              background: action.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              {React.cloneElement(action.icon as React.ReactElement, { size: 22, color: action.iconColor })}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '14px', fontWeight: 700, color: COLORS.gray800, margin: '0 0 4px' }}>
                {action.title}
              </p>
              <p style={{ fontSize: '12px', color: COLORS.gray500, margin: 0, lineHeight: 1.4 }}>
                {action.subtitle}
              </p>
            </div>
            <span style={{
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: 700,
              color: action.badgeColor,
              background: action.badgeBg,
              flexShrink: 0
            }}>
              {action.badge}
            </span>
            <ChevronRight size={18} color={COLORS.gray400} />
          </button>
        ))}
      </div>
    </div>
  );
});

// ============================================================
// MAIN HOME SCREEN
// ============================================================
const HomeScreen: React.FC = () => {
  const history = useHistory();
  const { user, isAuthenticated, isGuest, isHydrated } = useAuthStore();
  console.log('[HomeScreen] render auth state:', { isHydrated, isAuthenticated, isGuest, userId: user?.id });

  const [isLoading, setIsLoading] = useState(true);

  // ============================================================
  // BATCHED DASHBOARD DATA — single state object prevents 7
  // separate re-renders from individual setState calls.
  // ============================================================
  const [dashboardData, setDashboardData] = useState({
    retirementPlan: null as any,
    takafulPlan: null as any,
    assets: null as any,
    liabilities: null as any,
    transactions: null as any,
    zakatRecords: [] as any[],
    hajjPlans: [] as any[],
  });

  // Use useWealthData for assets/liabilities — single source of truth
  const {
    totalAssets: dbTotalAssets,
    totalLiabilities: dbTotalLiabilities,
    netWorth: dbNetWorth,
    refresh: refreshWealth
  } = useWealthData(true);

  // ============================================================
  // STABLE USER ID REF — prevents callback recreation
  // ============================================================
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    userIdRef.current = user?.id || null;
  }, [user?.id]);

  // ============================================================
  // MOUNT GUARD — prevents state updates after unmount
  // ============================================================
  const isMounted = useRef(true);
  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  // ============================================================
  // STABLE getCurrentUserId — no dependency churn
  // ============================================================
  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    if (userIdRef.current) {
      console.log('[HomeScreen] getCurrentUserId from userIdRef:', userIdRef.current);
      return userIdRef.current;
    }
    const globalId = (window as any).__NURWALLET_USER_ID__;
    if (globalId) {
      console.log('[HomeScreen] getCurrentUserId from globalId:', globalId);
      return globalId;
    }
    try {
      await capacitorDbService.init();
      const guestUser = await capacitorDbService.getUserByEmail('guest@nurwallet.local');
      if (guestUser) {
        (window as any).__NURWALLET_USER_ID__ = guestUser.id;
        return guestUser.id;
      }
    } catch (e) {}
    try {
      const allUsers = await capacitorDbService.getAllUsers?.();
      if (allUsers && allUsers.length > 0) {
        (window as any).__NURWALLET_USER_ID__ = allUsers[0].id;
        return allUsers[0].id;
      }
    } catch (e) {}
    return null;
  }, []); // EMPTY deps — stable forever

  // ============================================================
  // STABLE loadAllData — fetches ONLY non-wealth data
  // (wealth data comes from useWealthData hook)
  // ============================================================
  const lastLoadTime = useRef(0);
  const loadAllData = useCallback(async (source: string = 'init') => {
    if (!isMounted.current) return;

    // Debounce rapid calls (e.g. from wealthSync + visibilitychange)
    const now = Date.now();
    if (source !== 'refresh' && source !== 'manual' && now - lastLoadTime.current < 500) {
      console.log('[HomeScreen] loadAllData debounced');
      return;
    }
    lastLoadTime.current = now;

    // Skip if already loading (prevents duplicate concurrent loads)
    setIsLoading(prev => {
      if (prev && source !== 'refresh') return prev; // Already loading, don't reset
      return true;
    });

    try {
      const userId = await getCurrentUserId();
      if (!userId) {
        if (isMounted.current) setIsLoading(false);
        return;
      }

      // Fetch ONLY data NOT covered by useWealthData:
      // retirement, takaful, transactions, zakat, hajj
      // (assets/liabilities/netWorth come from useWealthData)
      const [retirement, takaful, txSummary, zakat, hajj] = await Promise.all([
        capacitorDbService.getRetirementPlan(userId),
        capacitorDbService.getTakafulPlan(userId),
        capacitorDbService.getTransactionSummary(userId).catch(() => ({ totalIncome: 0, totalExpense: 0, net: 0 })),
        capacitorDbService.getZakatRecords(userId).catch(() => []),
        capacitorDbService.getHajjPlans(userId).catch(() => []),
      ]);

      if (isMounted.current) {
        // Batch all updates into ONE setState to prevent 7 re-renders
        setDashboardData({
          retirementPlan: retirement,
          takafulPlan: takaful,
          transactions: txSummary,
          zakatRecords: zakat,
          hajjPlans: hajj,
          // Assets/liabilities are synced via useWealthData, but we keep
          // local state for retirement/takaful calculations that need the raw objects
          assets: { totalValue: dbTotalAssets },
          liabilities: { totalRemaining: dbTotalLiabilities },
        });
      }
    } catch (error: any) {
      console.error('[HomeScreen] Error:', error);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [getCurrentUserId, dbTotalAssets, dbTotalLiabilities]); // Stable deps

  // ============================================================
  // SINGLE MERGED EFFECT — replaces 3 separate useEffects
  // Prevents cascade re-renders from multiple subscriptions
  // ============================================================
  useEffect(() => {
    // Initial load
    loadAllData('mount');

    // Wealth sync listener (idempotent by ID)
    const unsubscribe = wealthSync.subscribeById('HomeScreen', () => {
      loadAllData('wealthSync');
    });

    // Page visibility listener
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadAllData('visibility');
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // App resume listener — refresh silently when app returns from background
    let removeAppListener: (() => void) | undefined;
    const setupAppListener = async () => {
      try {
        const { App } = await import('@capacitor/app');
        const listener = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            console.log('[HomeScreen] App resumed — refreshing data silently');
            refreshWealth(true).catch(() => {});
            loadAllData('resume');
          }
        });
        removeAppListener = listener.remove;
      } catch (e) {
        // App plugin not available (e.g. web preview) — ignore
      }
    };
    setupAppListener();

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
      removeAppListener?.();
    };
  }, []); // EMPTY deps — loadAllData is stable via useCallback

  // ============================================================
  // MEMOIZED CALCULATIONS — only recalculate when data changes
  // ============================================================
  const { retirementPlan, takafulPlan, assets, liabilities, transactions, zakatRecords, hajjPlans } = dashboardData;

  const retirementProj = useMemo(() => calculateRetirementProjection(retirementPlan), [retirementPlan]);
  const takafulScore = useMemo(() => calculateTakafulScore(takafulPlan), [takafulPlan]);

  const healthScore = useMemo(() => 
    calculateOverallHealth(retirementProj, takafulScore, assets, liabilities),
    [retirementProj, takafulScore, assets, liabilities]
  );

  const percentile = useMemo(() => 
    Math.max(5, Math.min(95, healthScore + Math.floor(Math.random() * 10) - 5)), 
    [healthScore] // Only changes when healthScore changes, not every render
  );

  // ============================================================
  // STABLE EVENT HANDLERS
  // ============================================================
  const handleProfile = useCallback(() => {
    history.push('/profile');
  }, [history]);

  const handleNotifications = useCallback(() => {
    history.push('/notifications');
  }, [history]);



  const userName = user?.fullName || 'Guest';
  const isPremium = user?.tier === 'premium';

  // ============================================================
  // MEMOIZED DATA ARRAYS — prevents child re-renders
  // ============================================================
  const atAGlanceItems = useMemo<AtAGlanceItem[]>(() => [
    {
      label: 'Total Assets',
      value: `RM ${dbTotalAssets.toLocaleString()}`,
      subValue: '8.4% this month',
      status: 'good',
      icon: <Wallet />,
      iconBg: COLORS.tealLight,
      iconColor: COLORS.teal
    },
    {
      label: 'Total Protection',
      value: `RM ${(takafulPlan?.existingCoverage || 0).toLocaleString()}`,
      subValue: takafulScore ? (takafulScore.progressPercent >= 70 ? 'On Track' : 'Needs Review') : 'Not Set',
      status: takafulScore ? (takafulScore.progressPercent >= 70 ? 'on-track' : 'needs-attention') : 'needs-attention',
      icon: <Shield />,
      iconBg: COLORS.purpleLight,
      iconColor: COLORS.purple
    },
    {
      label: 'Net Savings',
      value: `RM ${(transactions?.net || 0).toLocaleString()}`,
      subValue: '12.7% this month',
      status: 'good',
      icon: <PiggyBank />,
      iconBg: COLORS.blueLight,
      iconColor: COLORS.blue
    },
    {
      label: 'Emergency Fund',
      value: dbTotalAssets > 0 ? `${Math.round(dbTotalAssets / (transactions?.totalExpense || 1))} months` : 'Not Set',
      subValue: dbTotalAssets > 10000 ? 'On Track' : 'Below Target',
      status: dbTotalAssets > 10000 ? 'on-track' : 'below-target',
      icon: <Heart />,
      iconBg: COLORS.redLight,
      iconColor: COLORS.red
    }
  ], [dbTotalAssets, takafulPlan, takafulScore, transactions]);

  const pillars = useMemo<PillarItem[]>(() => [
    { 
      id: 'retirement', icon: <Landmark />, title: 'Retirement', 
      subtitle: retirementProj ? 'Good' : 'Getting Started', 
      color: COLORS.teal, bgColor: COLORS.tealLight, route: '/retirement',
      progress: retirementProj?.progressPercent || 0
    },
    { 
      id: 'protection', icon: <Shield />, title: 'Protection', 
      subtitle: takafulScore ? (takafulScore.progressPercent >= 70 ? 'Good' : 'Needs Review') : 'Not Set', 
      color: COLORS.purple, bgColor: COLORS.purpleLight, route: '/takaful',
      progress: takafulScore?.progressPercent || 0
    },
    { 
      id: 'legacy', icon: <Scale />, title: 'Legacy & Estate', 
      subtitle: 'Getting Started', 
      color: COLORS.indigo, bgColor: COLORS.indigoLight, route: '/legacy',
      progress: 20
    },
    { 
      id: 'education', icon: <GraduationCap />, title: 'Education', 
      subtitle: 'On Track', 
      color: COLORS.blue, bgColor: COLORS.blueLight, route: '/education',
      progress: 55
    },
    { 
      id: 'wealth', icon: <TrendingUp />, title: 'Wealth', 
      subtitle: 'Growing', 
      color: COLORS.amber, bgColor: COLORS.amberLight, route: '/wealth',
      progress: 60
    },
  ], [retirementProj, takafulScore]);

  const recommendedActions = useMemo<RecommendedAction[]>(() => [
    {
      id: 'takaful-review',
      icon: <Shield />,
      iconBg: COLORS.tealLight,
      iconColor: COLORS.teal,
      title: 'Review your takaful coverage',
      subtitle: 'Your coverage may be insufficient for your family needs.',
      badge: 'High Priority',
      badgeColor: COLORS.red,
      badgeBg: COLORS.redLight,
      route: '/takaful'
    },
    {
      id: 'wasiat',
      icon: <BookOpen />,
      iconBg: COLORS.purpleLight,
      iconColor: COLORS.purple,
      title: 'Create your will (Wasiat)',
      subtitle: 'Ensure your assets are distributed according to Shariah.',
      badge: 'Important',
      badgeColor: COLORS.purple,
      badgeBg: COLORS.purpleLight,
      route: '/legacy'
    },
    {
      id: 'retirement-save',
      icon: <PiggyBank />,
      iconBg: COLORS.amberLight,
      iconColor: COLORS.amber,
      title: 'Increase your retirement savings',
      subtitle: 'Small monthly increase can improve your future.',
      badge: 'Recommended',
      badgeColor: COLORS.amber,
      badgeBg: COLORS.amberLight,
      route: '/retirement'
    }
  ], []);

  const aiInsight = useMemo(() => ({
    title: 'Your retirement readiness can improve.',
    message: 'You may need an additional RM 320,000 to retire comfortably at age 60.',
    cta: 'Improve My Plan',
    route: '/retirement'
  }), []);

  // ============================================================
  // GUEST / UNAUTHENTICATED VIEW
  // CRITICAL: Wait for hydration before checking auth (iOS Preferences)
  // ============================================================
  if (!isHydrated) {
    return (
      <IonPage>
        <IonContent style={{ '--background': COLORS.creamLight } as React.CSSProperties}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            padding: '0 24px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #E5E7EB',
              borderTopColor: '#1B5A40',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
            <p style={{ marginTop: '16px', color: COLORS.gray500 }}>Loading...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!isAuthenticated && !user) {
    return (
      <IonPage>
        <IonContent style={{ '--background': COLORS.creamLight } as React.CSSProperties}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            padding: '0 24px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${COLORS.green}15, ${COLORS.lightGreen}15)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <LogIn size={40} color={COLORS.green} />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, color: COLORS.gray800, margin: '0 0 8px' }}>
              Welcome to NurWallet
            </h2>
            <p style={{ fontSize: '14px', color: COLORS.gray500, margin: '0 0 32px', maxWidth: '280px' }}>
              Login or create an account to see your personalized financial dashboard
            </p>
            <button
              onClick={() => history.push('/login')}
              style={{
                padding: '16px 32px',
                borderRadius: '16px',
                background: `linear-gradient(135deg, ${COLORS.deepGreen} 0%, ${COLORS.green} 100%)`,
                color: COLORS.white,
                fontSize: '16px',
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer',
                boxShadow: `0 4px 16px rgba(11, 62, 46, 0.3)`
              }}
            >
              Get Started
            </button>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // ============================================================
  // MAIN DASHBOARD VIEW
  // ============================================================
  return (
    <IonPage>
      <IonContent 
        className="ion-padding-bottom"
        style={{ 
          '--background': COLORS.creamLight,
          '--overflow': 'auto',
          '--padding-bottom': `max(100px, calc(80px + var(--sab, 0px)))`
        } as React.CSSProperties}
        scrollY={true}
      >
        <>
            <Header 
              userName={userName} 
              isPremium={isPremium} 
              onProfile={handleProfile}
              onNotifications={handleNotifications}
            />

            <FinancialHealthCard score={healthScore} percentile={percentile} isLoading={isLoading} />

            <AIInsightCard 
              title={aiInsight.title}
              message={aiInsight.message}
              cta={aiInsight.cta}
              route={aiInsight.route}
            />

            <AtAGlance items={atAGlanceItems} isLoading={isLoading} />

            <FinancialPillars pillars={pillars} />

            <RecommendedActions actions={recommendedActions} />
          </>

        <div style={{ height: '20px' }} />
      </IonContent>

      <style>{`
        @keyframes nw-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>


    </IonPage>
  );
};

export default HomeScreen;
